<template>
  <aw-product-list
    :is-whole-loading-completed="isWholeLoadingCompleted"
    :is-more-loading-completed="isMoreLoadingCompleted"
    :item-count="itemCount ?? undefined"
    is-sortable
    @load-more-product-items="loadMoreProductItems"
    @order-product-items="orderProductItems"
    @filter-product-items="filterProductItems"
  >
    <template #content>
      <aw-product-list-header v-if="itemCount" :product-counter="{ all: itemCount, loaded: products.length }">
        <template #listName>
          <lv-heading
            tag="h1"
            level="2"
            version2
            no-gutter
          >
            {{ $awt('aw.loyalty_offer.title') }}
          </lv-heading>
        </template>
        <template #filters>
          <client-only>
            <aw-category-selection-filter
              v-if="screenRange['mobile-max'] && hasCategorySelectionFilter"
              view="COMPACT"
              @activate-filter="filterProductItems"
            />
            <aw-product-list-order-by-and-mobile-filter-modal
              v-else-if="screenRange['tablet-min']"
              is-sortable
              :is-filterable="false"
              controls-visible-status
            />
          </client-only>
        </template>
      </aw-product-list-header>
    </template>
  </aw-product-list>
</template>

<script setup lang="ts">
  import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue';
  import { refreshNuxtData, useAsyncData, useNuxtApp, useRoute } from 'nuxt/app';
  import { fetchKey } from '~~/common/utils/awRunLegacyAsyncData';

  import { LvHeading } from '~~/common/components/loginet-vue-shop/index';
  import AwProductList from '~~/common/components/Page/ProductList/AwProductList.vue';
  import AwProductListHeader from '~~/common/components/Page/ProductList/AwProductListHeader.vue';
  import {
    useIsReturningEnabled,
    useIsReturningFromProductPage,
  } from '~~/common/composables/useProductListHistory';
  import { createScrollToProductId } from '~~/common/utils/productListHistoryScroll';
  import { useCategorySelectionFilter } from '~~/common/composables/useCategorySelectionFilter';

  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useCategoryStore } from '~~/common/stores/category';
  import { useProductsStore, type ProductQueryParams } from '~~/common/stores/products';
  import { useDeliveryStore } from '~~/shop/app/stores/delivery';
  import { isApiError } from '~~/common/utils/apiError';
  import { defineI18nRoute } from '#i18n';

  const AwProductListOrderByAndMobileFilterModal = defineAsyncComponent(() => import('~~/common/components/Page/ProductList/AwProductListOrderByAndMobileFilterModal.vue'));
  const AwCategorySelectionFilter = defineAsyncComponent(() => import('~~/common/components/Page/ProductList/AwCategorySelectionFilter.vue'));

  defineOptions({
    name: 'LoyaltyOffersPage',
  });

  // eslint-disable-next-line no-undef
  definePageMeta({
    layout: 'dynamic-content',
  });
  false && /*#__PURE__*/ defineI18nRoute({
    paths: {
      en: '/shop/loyalty-offers',
      hu: '/shop/bizalomkartyas-ajanlatok',
    },
  });

  const { $logger } = useNuxtApp();
  const route = useRoute();
  const userInterfaceStore = useUserInterfaceStore();
  const productsStore = useProductsStore();
  const deliveryStore = useDeliveryStore();

  const isReturningFromProductPage = useIsReturningFromProductPage();
  const isReturningEnabled = useIsReturningEnabled();

  const { hasCategorySelectionFilter } = useCategorySelectionFilter();

  const { data: offerListName } = await useAsyncData(
    fetchKey(),
    async () => {
      try {
        const listName = route.query?.list_name ? String(route.query.list_name) : 'current';
        if (isReturningFromProductPage.value && isReturningEnabled.value) {
          productsStore.restoreFromProductListHistoryInformation();
        } else {
          // Restore the filters and sorting the `qq` parameter carries, the way the
          // category and boutique branches of AwPage do: this page is routed on its
          // own, so nothing else feeds the store from the URL and a reload would
          // otherwise render the unfiltered list under a filtered address.
          productsStore.setFilterFromQuery(route.query);
          await productsStore.fetchProducts({
            loyaltyOffer: listName,
            isCached: true,
            itemsPerPage: productsStore.itemsPerPage || 16,
            page: 1,
            filterParams: productsStore.filterBy,
            sortKey: productsStore.sortKey || null,
            sortBy: productsStore.sortBy || null,
          });
        }
        return listName;
      } catch (error) {
        if (!isApiError(error) || error.response?.status !== 404) {
          $logger.error(error);
        }
        return null;
      }
    },
    { default: () => null },
  );

  const isWholeLoadingCompleted = ref(true);
  const isMoreLoadingCompleted = ref(true);

  const screenRange = computed(() => userInterfaceStore.mediaQueries);
  const currentPage = computed(() => productsStore.currentPage);
  const itemCount = computed(() => productsStore.itemCount);
  const itemsPerPage = computed(() => productsStore.itemsPerPage);
  const sortKey = computed(() => productsStore.sortKey);
  const sortBy = computed(() => productsStore.sortBy);
  const filterBy = computed(() => productsStore.filterBy);
  const products = computed(() => productsStore.renderedProducts);
  const deliverySetup = computed(() => deliveryStore.setup);

  const productBaseQuery = computed<ProductQueryParams>(() => {
    const data: ProductQueryParams = {
      isCached: true,
      loyaltyOffer: offerListName.value ?? undefined,
      itemsPerPage: itemsPerPage.value,
      page: currentPage.value,
      filterParams: filterBy.value,
    };
    if (sortKey.value) {
      data.sortKey = sortKey.value;
    }
    if (sortBy.value) {
      data.sortBy = sortBy.value;
    }
    return data;
  });

  watch(deliverySetup, () => {
    refreshNuxtData();
  });

  // Refetch when a filter is cleared from the active-filter bar, which only
  // bumps this counter instead of emitting to the page.
  watch(
    () => productsStore.refreshProducts,
    () => {
      filterProductItems();
    },
  );

  onMounted(() => {
    const categoryStore = useCategoryStore();
    categoryStore.setTreeId(null);
    const productId = productsStore.productListHistoryInformation?.productId;
    if (isReturningFromProductPage.value && productId !== undefined) {
      const scrollToProductId = createScrollToProductId();
      scrollToProductId(productId);
    }
  });

  async function loadMoreProductItems (loadingState?: { complete: () => void; loaded: () => void }) {
    isMoreLoadingCompleted.value = false;

    if (loadingState && itemCount.value === products.value.length) {
      loadingState.complete();
      return;
    }

    const result = await productsStore.fetchProducts({
      ...productBaseQuery.value,
      page: currentPage.value + 1,
    });

    isMoreLoadingCompleted.value = true;
    if (loadingState) {
      if (!result || itemCount.value === products.value.length) {
        loadingState.complete();
      } else {
        loadingState.loaded();
      }
    }
  }
  async function orderProductItems (orderParams: { value: ProductQueryParams }) {
    isMoreLoadingCompleted.value = false;
    let data: ProductQueryParams = {
      ...productBaseQuery.value,
      itemsPerPage: currentPage.value * (itemsPerPage.value ?? 0),
      page: 1,
    };

    if (sortKey.value) {
      delete data.sortKey;
    }
    if (sortBy.value) {
      delete data.sortBy;
    }

    data = {
      ...data,
      ...orderParams.value,
    };

    await productsStore.fetchProducts(data);
    isMoreLoadingCompleted.value = true;
  }
  async function filterProductItems () {
    isMoreLoadingCompleted.value = false;
    await productsStore.fetchProducts({
      ...productBaseQuery.value,
      itemsPerPage: currentPage.value * (itemsPerPage.value ?? 0),
      page: 1,
    });
    isMoreLoadingCompleted.value = true;
  }
</script>
